import React from 'react';
import { motion } from 'framer-motion';
import { Package, Search, Clock, CheckCircle, Truck, AlertCircle, MapPin, User, Euro } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { useLanguage } from '@/lib/language';
import { useState, useEffect, useCallback } from 'react';

interface OrderData {
  id: string;
  order_number: string;
  sender_name: string;
  sender_phone: string;
  sender_email: string;
  sender_address: string;
  recipient_name: string;
  recipient_phone: string;
  recipient_address: string;
  amount: number;
  currency: string;
  delivery_type: string;
  service_fee: number;
  insurance_fee: number;
  total_amount: number;
  status: string;
  created_at: string;
  notes?: string;
}

const statusConfig = {
  // Angielskie statusy
  unpaid: {
    label: 'tracking.status.unpaid',
    icon: AlertCircle,
    color: "bg-orange-500/10 text-orange-600 border-orange-500/20",
    description: 'tracking.status.unpaid'
  },
  pending: {
    label: 'tracking.status.pending',
    icon: Clock,
    color: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
    description: 'tracking.status.pending'
  },
  confirmed: {
    label: 'tracking.status.confirmed',
    icon: CheckCircle,
    color: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    description: 'tracking.status.confirmed'
  },
  in_transit: {
    label: 'tracking.status.inTransit',
    icon: Truck,
    color: "bg-purple-500/10 text-purple-600 border-purple-500/20",
    description: 'tracking.status.inTransit'
  },
  delivered: {
    label: 'tracking.status.delivered',
    icon: CheckCircle,
    color: "bg-green-500/10 text-green-600 border-green-500/20",
    description: 'tracking.status.delivered'
  },
  cancelled: {
    label: 'tracking.status.cancelled',
    icon: AlertCircle,
    color: "bg-red-500/10 text-red-600 border-red-500/20",
    description: 'tracking.status.cancelled'
  },
  // Polskie statusy (mapowanie)
  'nieopłacone': {
    label: 'tracking.status.unpaid',
    icon: AlertCircle,
    color: "bg-orange-500/10 text-orange-600 border-orange-500/20",
    description: 'tracking.status.unpaid'
  },
  'oczekuje potwierdzenia': {
    label: 'tracking.status.pending',
    icon: Clock,
    color: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
    description: 'tracking.status.pending'
  },
  'potwierdzone': {
    label: 'tracking.status.confirmed',
    icon: CheckCircle,
    color: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    description: 'tracking.status.confirmed'
  },
  'w drodze': {
    label: 'tracking.status.inTransit',
    icon: Truck,
    color: "bg-purple-500/10 text-purple-600 border-purple-500/20",
    description: 'tracking.status.inTransit'
  },
  'dostarczone': {
    label: 'tracking.status.delivered',
    icon: CheckCircle,
    color: "bg-green-500/10 text-green-600 border-green-500/20",
    description: 'tracking.status.delivered'
  },
  'anulowane': {
    label: 'tracking.status.cancelled',
    icon: AlertCircle,
    color: "bg-red-500/10 text-red-600 border-red-500/20",
    description: 'tracking.status.cancelled'
  }
};

export default function Tracking() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchStatus, setSearchStatus] = useState<'idle' | 'found' | 'not_found' | 'error'>('idle');

  const handleSearchWithNumber = useCallback(async (number: string) => {
    console.log('Searching for order:', number);
    if (!number.trim()) return;

    setIsSearching(true);
    setSearchStatus('idle');

    try {
      const { data, error } = await supabase
        .from('cash_orders_20260303')
        .select('*')
        .eq('order_number', number.trim().toUpperCase())
        .single();

      console.log('Search result:', { data, error });
      
      if (error) {
        if (error.code === 'PGRST116') {
          setSearchStatus('not_found');
          setOrderData(null);
        } else {
          console.error('Search error:', error);
          setSearchStatus('error');
        }
      } else {
        setOrderData(data);
        setSearchStatus('found');
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchStatus('error');
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Automatyczne wyszukiwanie z URL
  useEffect(() => {
    const orderParam = searchParams.get('order');
    if (orderParam) {
      setTrackingNumber(orderParam);
      // Automatyczne wyszukiwanie po załadowaniu
      setTimeout(() => {
        handleSearchWithNumber(orderParam);
      }, 500);
    }
  }, [searchParams, handleSearchWithNumber]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSearchWithNumber(trackingNumber);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusConfig = (status: string) => {
    return statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
  };

  // Funkcja odświeżania danych zamówienia
  const refreshOrderData = async () => {
    if (orderData?.order_number) {
      await handleSearchWithNumber(orderData.order_number);
    }
  };

  // Automatyczne odświeżanie co 30 sekund gdy zamówienie jest wyświetlane
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (searchStatus === 'found' && orderData) {
      interval = setInterval(() => {
        refreshOrderData();
      }, 30000); // 30 sekund
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [searchStatus, orderData]);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Package className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-bold">{t('tracking.title')}</h1>
            </div>
            <p className="text-muted-foreground">
              {t('tracking.subtitle')}
            </p>
          </div>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                {t('tracking.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder={t('tracking.placeholder')}
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    className="flex-1"
                    disabled={isSearching}
                  />
                  <Button type="submit" disabled={isSearching || !trackingNumber.trim()}>
                    {isSearching ? t('common.loading') : t('tracking.searchButton')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Wyniki wyszukiwania */}
          {searchStatus === 'found' && orderData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl">{t('tracking.orderNumber')} {orderData.order_number}</CardTitle>
                      <CardDescription>
                        {t('tracking.orderDate')}: {formatDate(orderData.created_at)}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={refreshOrderData}
                        disabled={isSearching}
                      >
                        {isSearching ? t('common.loading') : t('tracking.refreshStatus')}
                      </Button>
                      <Badge className={getStatusConfig(orderData.status).color}>
                        {React.createElement(getStatusConfig(orderData.status).icon, { className: "w-4 h-4 mr-2" })}
                        {t(getStatusConfig(orderData.status).label)}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">{t('common.status')}:</p>
                    <p className="font-medium">{t(getStatusConfig(orderData.status).label)}</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {t('tracking.applicant')}
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p><strong>{t('common.name')}:</strong> {orderData.sender_name}</p>
                        <p><strong>{t('common.phone')}:</strong> {orderData.sender_phone}</p>
                        <p><strong>{t('common.email')}:</strong> {orderData.sender_email}</p>
                        <div>
                          <strong>{t('common.address')}:</strong>
                          <p className="text-muted-foreground">{orderData.sender_address}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Euro className="w-4 h-4" />
                        {t('tracking.amount')}
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>{t('tracking.amount')}:</span>
                          <span className="font-mono">{orderData.amount.toFixed(2)} {orderData.currency}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('order.serviceFee')}:</span>
                          <span className="font-mono">{orderData.service_fee.toFixed(2)} {orderData.currency}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('order.insurance')}:</span>
                          <span className="font-mono">{orderData.insurance_fee.toFixed(2)} {orderData.currency}</span>
                        </div>
                        <div className="flex justify-between font-semibold border-t pt-2">
                          <span>{t('tracking.totalAmount')}:</span>
                          <span className="font-mono">{orderData.total_amount.toFixed(2)} {orderData.currency}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {t('tracking.deliveryType')}
                    </h3>
                    <p className="text-sm">
                      {orderData.delivery_type === 'express' ? t('order.express') : t('order.standard')}
                    </p>
                  </div>

                  {orderData.notes && (
                    <div>
                      <h3 className="font-semibold mb-3">{t('common.notes')}</h3>
                      <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded">
                        {orderData.notes}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {searchStatus === 'not_found' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
            >
              <Card>
                <CardContent className="text-center py-8">
                  <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">{t('tracking.notFound')}</h3>
                  <p className="text-muted-foreground mb-6">
                    {t('tracking.contactUs')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <Button variant="outline" size="sm" asChild>
                      <a href="tel:+48222639191">
                        {t('tracking.callButton')} (PL): +48 22 263 91 91
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href="tel:+41415331634">
                        {t('tracking.callButton')} (CH): +41 415 331 634
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href="mailto:support@expresshub.pro">
                        {t('tracking.emailButton')}
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {searchStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
            >
              <Card>
                <CardContent className="text-center py-8">
                  <AlertCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
                  <h3 className="text-lg font-semibold mb-2">{t('tracking.error')}</h3>
                  <p className="text-muted-foreground">
                    {t('tracking.contactUs')}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}